import { Link } from "react-router-dom";
import QuoteForm from "./QuoteForm";
import HeroProviderCluster from "./HeroProviderCluster";
import HeroAccent from "./HeroAccent";
import SourcesPopover from "./SourcesPopover";
import { Info, BookOpenCheck, Fingerprint, FlaskConical } from "lucide-react";
import { useRatesFreshness } from "../lib/freshness";
import { useDataField } from "../lib/dataSources";
import { useHeroOverrides } from "../lib/homepageData";

// Iter-85 — Hero stat values hydrate from the editorial data sources registry.
// Iter-101 — KPI tile UX refinement (breathable padding, clean methodology row).
// Iter-102/103 — Unified "Verified · 3 sources" pill via shared <SourcesPopover>.
// Iter-174 — Per-stat hierarchy strengthened. Suffix is now part of the
//            primary value display + description sits below at a clear
//            secondary weight. Numbers count up on first viewport entry.
// Iter-268.F — Each tile now ships a `defaultValue` matching the
//            authoritative MongoDB seed value, so the brief loading
//            state shows the REAL number instead of an em-dash
//            placeholder. On a slow mobile connection, the
//            data-sources fetch can take 300-800ms — long enough to
//            screenshot. The em-dash made users think the tiles were
//            broken. The DB value still wins the moment the fetch
//            resolves (overrides the literal in <2 frames), so admin
//            edits propagate normally and there's no copy drift risk.
const HERO_STAT_KEYS = [
  {
    key: "hero.insurance_savings",
    inlineSuffix: "/year",
    description: "Potential auto insurance savings",
    tileLabel: "Insurance savings",
    defaultValue: "$1,100",
  },
  {
    key: "hero.refinance_savings",
    inlineSuffix: "",
    // Modelled example ($22k, 48 mo, 9.2%→7.3% APR), not an average — labelled as such.
    description: "Example refinance savings",
    tileLabel: "Refinance savings",
    defaultValue: "$1,640",
  },
];

// Iter-454 — Compact financial evidence: one shared two-cell panel, static
// figures (no count-up, no hover motion). Each figure still links to its
// methodology anchor (FTC click-path requirement).
function HeroStatTile({ statKey, inlineSuffix, description, defaultValue }) {
  const { field, ready } = useDataField(statKey);
  const value = ready && field?.value ? field.value : (defaultValue || "—");
  return (
    <Link
      to="/methodology/savings#headline-numbers"
      data-testid={`hero-stat-${statKey.replace(/[^a-z0-9]/gi, "-")}-methodology-link`}
      aria-label={`${value}${inlineSuffix || ""} ${description} — see methodology`}
      className="flex flex-col items-center justify-center text-center h-full px-4 py-3.5 sm:px-5 sm:py-4 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#10B981]"
    >
      <span className="flex items-baseline justify-center font-display font-black text-[20px] sm:text-[22px] lg:text-2xl text-slate-950 leading-none tracking-tight tabular-nums">
        <span data-testid={`hero-stat-number-${statKey.replace(/[^a-z0-9]/gi, "-")}`}>{value}</span>
        {inlineSuffix && <span className="text-[12px] leading-none font-bold text-slate-500 ml-0.5">{inlineSuffix}</span>}
      </span>
      <span className="block text-[11px] text-slate-600 font-semibold leading-snug mt-1.5">{description}</span>
    </Link>
  );
}


export default function Hero() {
  const freshnessLabel = useRatesFreshness();
  // Pre-resolve hero source fields so the shared popover gets pure data.
  const insurance = useDataField("hero.insurance_savings");
  const refinance = useDataField("hero.refinance_savings");
  // "2 Minutes" tile removed (Iter-453): speed already lives in the H1 and the form badge.
  const sources = [
    { tileLabel: "Insurance savings", ...insurance },
    { tileLabel: "Refinance savings", ...refinance },
  ];

  // Iter-189 Sprint 4 — Hero copy overrides. Admin edits from
  // /admin?tab=homepage-cms surface here via the merged
  // `/api/homepage` payload. Each `||` falls back to the existing
  // hardcoded JSX literal when no override exists — keeps the
  // 2,453-test regex suite green AND keeps SSR fast.
  const heroOverrides = useHeroOverrides();

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#FAFAFA]">
      {/* Fintech-inspired animated background (CSS-only, GPU-cheap, reduced-motion aware) */}
      <div aria-hidden="true" className="hero-bg" data-testid="hero-animated-bg">
        <div className="hero-bg-aurora" />
        <div className="hero-bg-grid" />
        <div className="hero-bg-orb hero-bg-orb-a" />
        <div className="hero-bg-orb hero-bg-orb-b" />
        <div className="hero-bg-meridian" />
        <div className="hero-bg-fade" />
      </div>

      <div className="max-w-wide mx-auto px-6 lg:px-10 pt-5 lg:pt-16 pb-7 lg:pb-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-16 items-start">
          {/* lg:pt-8 optically aligns the H1 cap line just under the form badge, level with its step row. */}
          <div className="lg:col-span-7 lg:pt-8 relative">
            {/* Iter-440 — desktop-only insurer app cluster beside "In 2 minutes." (xl+, decorative). */}
            <HeroProviderCluster />
            {/* Iter-185.P — Speedometer moved OUT of the absolute corner
                placement. It now lives BETWEEN the subhead and the
                trust pills row as a centered flow element. See
                HeroAccent.jsx for the wide half-circle redesign. */}
            {/* Iter-182.1 — Bumped mobile H1 from text-4xl (36px) to
                text-[42px] so the headline fills more of the 390px
                viewport width. The iter-182 first-draft left a weird
                right-side whitespace because text-4xl wrapped to 3
                short lines that didn't use the full mobile column.
                Iter-185.O dropped to text-[38px] per user feedback;
                Iter-185.P bumped slightly back up to text-[40px];
                Iter-185.Q bumped to text-[44px] AND added text-center
                lg:text-left so the headline is horizontally centered
                on mobile per user feedback. */}
            {/* Iter-185.S — H1 mobile bumped text-[44px] → text-[46px]
                per user "Please increase header and sub header text
                size slightly from current level." Desktop chain
                preserved. */}
            {/* Iter-185.AC — Mobile H1 leading + tracking polish per
                the AB audit (S1). Mobile leading bumped 0.92 → 1.0
                (was crashing lines together on 4-line headline);
                sm:leading bumped 0.9 → 0.95. Tracking stays
                `tracking-tighter` which compiles to -0.025em — within
                spec of the audit's -0.02em recommendation. The H1
                still reads dense + premium-fintech tight, just no
                longer claustrophobic on iPhone-12-width. */}
            <h1 className="font-display font-black text-[46px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.0] sm:leading-[0.95] tracking-tighter text-slate-950 mb-2 lg:mb-7 text-balance text-center lg:text-left relative z-10">
              {heroOverrides.headline_lead || "Save money on"}{" "}
              <span className="lg:block">{heroOverrides.headline_subject || "car ownership."}</span>{" "}
              <span className="relative inline-block">
                {heroOverrides.headline_emphasis || "In 2 minutes"}
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#10B981] -z-10" />
              </span>
              .
            </h1>

            <p className="text-[18px] md:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-[360px] sm:max-w-xl lg:max-w-xl mb-4 lg:mb-10 mx-auto lg:mx-0 text-center lg:text-left lg:text-balance">
              {/* Iter-185.O — Subhead copy simplified per user
                  feedback. Iter-189 wires admin overrides above the
                  hardcoded JSX literals; the overrides fall back to
                  the original copy when no override exists. */}
              <span className="lg:hidden">{heroOverrides.subhead_mobile || "Compare insurance, loans, refinancing, and warranties — all in one place."}</span>
              <span className="hidden lg:inline">{heroOverrides.subhead_desktop || "Compare auto insurance quotes, explore car loan and refinancing options, and lower the cost of owning your car — all in one place. Free to use. No spam calls."}</span>
            </p>

            {/* Iter-185.P — Premium mobile speedometer moved INTO the
                flow between subhead and trust pills, centered.
                Replaces the iter-185.O absolute-positioned variant
                per user feedback: "I want speedometer to be moved
                and centered under sub header text above soft credit
                and editor vetted." */}
            <HeroAccent />

            {/* Iter-185.AB — Trust pill row alignment fix.
                User audit follow-up: after the iter-185.AA box-strip,
                the pills landed left-anchored in their grid cells
                (because the inherited `px-3 py-2` mobile padding was
                kept) which made the two icons sit at different
                horizontal positions relative to their containing
                cells. User: "please align the Soft credit pull and
                Editor-vetted on mobile homepage hero. right now it
                looks a little awkward."

                Fix: mobile padding dropped to `px-0 py-1` (was
                `px-3 py-2`) AND `justify-center` added so each pill
                self-centers inside its 50% grid cell. The two icons
                + labels now land symmetrically across the row's
                vertical centerline — same visual balance the boxed
                version had, just without the box.

                Desktop variant unchanged (still uses `lg:px-0
                lg:py-0` + `lg:justify-start`-implicit flex). */}
            <div
              data-testid="hero-trust-row"
              className="grid grid-cols-2 gap-2.5 lg:flex lg:flex-wrap lg:items-center lg:gap-x-3 min-[1400px]:gap-x-6 lg:gap-y-3 text-[11px] lg:text-sm text-slate-700"
            >
              <div
                data-testid="hero-trust-soft-credit"
                className="flex items-center justify-center lg:justify-start gap-2.5 lg:gap-2 px-0 py-1 lg:py-0 bg-transparent border-0 min-w-0"
              >
                <Fingerprint
                  aria-hidden
                  className="w-4 h-4 text-[#10B981] lg:text-[#0A2540] shrink-0"
                  strokeWidth={2.25}
                />
                <span className="font-bold text-[11px] lg:text-sm tracking-tight text-slate-900 truncate">
                  <span className="lg:hidden">Soft credit pull</span>
                  <span className="hidden lg:inline">Soft credit pull options</span>
                </span>
              </div>
              {/* Iter-182 — Third trust signal: editor-vetted providers.
                  Reinforces the iter-181-E methodology rubrics + the
                  named editorial team page at /about/editorial-team. */}
              <Link
                to="/about/editorial-team"
                data-testid="hero-trust-editor-vetted"
                className="flex items-center justify-center lg:justify-start gap-2.5 lg:gap-2 px-0 py-1 lg:py-0 bg-transparent border-0 min-w-0 transition-colors lg:hover:text-slate-950 underline-offset-2 lg:hover:underline"
              >
                <BookOpenCheck
                  aria-hidden
                  className="w-4 h-4 text-[#10B981] lg:text-[#0A2540] shrink-0"
                  strokeWidth={2.25}
                />
                <span className="font-bold text-[11px] lg:text-sm tracking-tight text-slate-900 lg:text-slate-700 truncate">
                  <span className="lg:hidden">Editor-vetted</span>
                  <span className="hidden lg:inline">Editor-vetted providers</span>
                </span>
              </Link>
              <Link
                to="/how-we-make-money"
                data-testid="hero-how-we-make-money"
                className="hidden lg:inline-flex items-center gap-2 text-slate-500 hover:text-slate-950 underline-offset-2 hover:underline transition-colors"
              >
                <Info className="w-4 h-4" />
                <span className="font-semibold text-xs">
                  How we make money
                </span>
              </Link>
              {/* Iter-278.O — Subtle entry-point to the /data research
                  hub. Same understated styling as "How we make money"
                  so it doesn't disrupt the hero hierarchy; goal is
                  3-5% of homepage visitors discover the research. */}
              <Link
                to="/data"
                data-testid="hero-research-link"
                className="hidden lg:inline-flex items-center gap-2 text-slate-500 hover:text-slate-950 underline-offset-2 hover:underline transition-colors"
              >
                <FlaskConical className="w-4 h-4" />
                <span className="font-semibold text-xs">
                  Read our research
                </span>
              </Link>
            </div>

            {/* Iter-185.AD — Mobile-only affiliate disclosure entry.
                User audit: "On mobile version homepage main header,
                can we move How we make money down slightly from
                current position, just a little bit lower then it
                is now would look great." Margin-top bumped mt-3 →
                mt-5 (12px → 20px) for the extra breathing room. */}
            <Link
              to="/how-we-make-money"
              data-testid="hero-how-we-make-money-mobile"
              className="lg:hidden inline-flex items-center justify-center gap-1.5 mt-5 mx-auto text-slate-500 hover:text-slate-950 underline-offset-2 hover:underline transition-colors w-full"
            >
              <Info className="w-3.5 h-3.5" />
              <span className="font-semibold text-[11px]">
                How we make money
              </span>
            </Link>
          </div>

          <div className="lg:col-span-5 lg:pt-2 mt-4 lg:mt-0">
            {/* Iter-185.N — Removed the iter-185.A mobile $487 savings
                anchor strip per user feedback. The $487 figure still
                surfaces below the fold in the hero stat grid. This
                cleans up vertical density above the form so the
                speedometer + H1 read as the dominant accent. */}

            <QuoteForm />

            <div
              data-testid="hero-stat-grid"
              className="mt-4 grid grid-cols-2 divide-x divide-slate-200 border border-slate-200 bg-white text-left"
            >
              {HERO_STAT_KEYS.map((s) => (
                <HeroStatTile
                  key={s.key}
                  statKey={s.key}
                  inlineSuffix={s.inlineSuffix}
                  description={s.description}
                  defaultValue={s.defaultValue}
                />
              ))}
            </div>

            <div
              data-testid="hero-stats-freshness"
              className="mt-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700"
            >
              <SourcesPopover sources={sources} tone="light" testid="hero-sources-pill" />
              <span aria-hidden className="text-slate-300">·</span>
              <span className="whitespace-nowrap text-slate-500">
                {freshnessLabel}
              </span>
              <span aria-hidden className="text-slate-300">·</span>
              <Link
                to="/methodology"
                data-testid="hero-stats-methodology-link"
                className="whitespace-nowrap underline-offset-2 hover:underline hover:text-emerald-800 transition-colors"
              >
                See methodology
              </Link>
            </div>
            <p data-testid="hero-insurance-savings-disclosure" className="mt-2 text-center text-[11px] leading-snug text-slate-500">
              $1,100: Insurify average potential savings based on initial quotes. Actual savings vary and are not guaranteed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
